/**
 * The arithmetic behind the plan's objects — radiators, the outdoor unit, pipe
 * runs and fittings — kept apart from the components that draw them so every
 * rule here can be checked with plain numbers (`plan-objects.test.ts`).
 *
 * All in plan metres, on the scan's own frame: x = world X, y = world Z, which
 * is screen-down (see `scan-plan.ts`). A rotation is degrees CLOCKWISE on
 * screen, the direction SVG's `rotate()` turns with y pointing down. At 0° an
 * object's length runs along +x and its depth along +y; its local axes are
 *
 *   u = ( cos θ, sin θ)   along the length
 *   v = (−sin θ, cos θ)   across the depth — the FRONT of an outdoor unit
 */

import {
  PLAN_OBJECT_KIND_NAMES,
  heatPumpCaption as calcHeatPumpCaption,
  normaliseDegrees,
  type PlanWall,
} from "@sts/calc";
import { closestPointOnSegment as closestPointOnSegmentM } from "@sts/geometry";
import {
  FITTING_NAMES,
  PIPE_MATERIAL_NAMES,
  PLAN_PIPE_MATERIALS,
  fittingSizeLabel,
  pipeMaterialOf,
  radiatorLabel,
  vesselLabel,
  type FittingSpec,
  type HeatPumpSpec,
  type PipeDiameterMm,
  type PipeSpec,
  type PlacedGeometry,
  type PlanBufferSpec,
  type PlanCylinderSpec,
  type PlanObjectPoint,
  type PlanPipeMaterial,
  type RadiatorSpec,
} from "@sts/rules-data";
import { formatLength } from "@sts/scan-viewer";
import type { PlanObject } from "../api/types.js";

type Point = PlanObjectPoint;

const DEG = Math.PI / 180;

// The shapes themselves — rotation, corners, footprint, which side of a wall
// is its room — moved to @sts/calc so the design check can run them in the API
// too. Re-exported here so the drawing's imports are unchanged.
export {
  axesOf,
  footprintM,
  normaliseDegrees,
  placedCorners,
  wallsFacingRooms,
  type PlanWall,
} from "@sts/calc";

interface NearestWall {
  readonly wall: PlanWall;
  /** The point on the wall line nearest the cursor. */
  readonly foot: Point;
  readonly distanceM: number;
}

/**
 * The wall nearest `point` within `radiusM`, preferring one whose ROOM side the
 * point is on: where two rooms' walls run back to back, a cursor in the
 * kitchen means the kitchen's wall even if the hall's is a hair nearer.
 */
export function nearestWall(
  point: Point,
  walls: readonly PlanWall[],
  radiusM: number,
): NearestWall | undefined {
  // The price, in metres, of being on the wrong side of a wall — about one
  // wall thickness, so the room-side wall wins a near tie and nothing else.
  const WRONG_SIDE_PENALTY_M = 0.2;
  let best: NearestWall | undefined;
  let bestScore = Infinity;
  for (const wall of walls) {
    const foot = closestPointOnSegmentM(point, wall.start, wall.end);
    const distanceM = Math.hypot(point.x - foot.x, point.y - foot.y);
    if (distanceM > radiusM) continue;
    const side = (point.x - foot.x) * wall.inward.x + (point.y - foot.y) * wall.inward.y;
    const score = distanceM + (side < -1e-6 ? WRONG_SIDE_PENALTY_M : 0);
    if (score < bestScore) {
      bestScore = score;
      best = { wall, foot, distanceM };
    }
  }
  return best;
}

/** The rotation that runs an object's length along a wall. */
function alongWallDeg(wall: PlanWall): number {
  return normaliseDegrees(Math.atan2(wall.end.y - wall.start.y, wall.end.x - wall.start.x) / DEG);
}

export interface RadiatorPlacement {
  readonly geometry: PlacedGeometry;
  /** The wall it was put against, or undefined when it stands free. */
  readonly wallId?: string;
}

/**
 * Where a radiator dropped at `point` goes.
 *
 * Near a wall (within `radiusM`) it goes flat against that wall's room-side
 * face: length along the wall, its back on the wall line, its body in the
 * room, slid along so it does not overhang the wall's ends (centred on a wall
 * shorter than itself). RoomPlan's wall line is the room-facing surface, so
 * "back on the line" is "back on the plaster". Otherwise it stays exactly
 * where it was dropped, at `freeRotationDeg`.
 */
export function placeRadiator(
  point: Point,
  lengthM: number,
  depthM: number,
  walls: readonly PlanWall[],
  radiusM: number,
  freeRotationDeg = 0,
): RadiatorPlacement {
  const near = nearestWall(point, walls, radiusM);
  if (!near) return { geometry: { x: point.x, y: point.y, rotationDeg: normaliseDegrees(freeRotationDeg) } };
  const { wall } = near;
  const dx = wall.end.x - wall.start.x;
  const dy = wall.end.y - wall.start.y;
  const wallLength = Math.hypot(dx, dy);
  const along = { x: dx / wallLength, y: dy / wallLength };
  const t = (near.foot.x - wall.start.x) * along.x + (near.foot.y - wall.start.y) * along.y;
  const half = lengthM / 2;
  const centreT = wallLength >= lengthM ? Math.min(Math.max(t, half), wallLength - half) : wallLength / 2;
  return {
    geometry: {
      x: wall.start.x + along.x * centreT + wall.inward.x * (depthM / 2),
      y: wall.start.y + along.y * centreT + wall.inward.y * (depthM / 2),
      rotationDeg: alongWallDeg(wall),
    },
    wallId: wall.id,
  };
}

/**
 * The rotation an outdoor unit dropped at `point` should take: its back to the
 * nearest wall within `radiusM` and its front (v) facing away from the room —
 * that is, out of the building. Position is left where it was dropped: the
 * clearance a unit needs from the wall is the manufacturer's to state, not
 * this drawing's to assume. Undefined with no wall in reach.
 */
export function outdoorUnitRotation(
  point: Point,
  walls: readonly PlanWall[],
  radiusM: number,
): number | undefined {
  const near = nearestWall(point, walls, radiusM);
  if (!near) return undefined;
  // v = (−sin θ, cos θ) must equal the outward normal (−inward).
  const out = { x: -near.wall.inward.x, y: -near.wall.inward.y };
  return normaliseDegrees(Math.atan2(-out.x, out.y) / DEG);
}

// ---------------------------------------------------------------- pipes ----

/** The length of a run, metres: the sum of its straight legs. */
export function runLengthM(points: readonly Point[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1] as Point;
    const b = points[i] as Point;
    total += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return total;
}

export interface PipeTotal {
  readonly diameterMm: PipeDiameterMm;
  readonly material: PlanPipeMaterial;
  readonly flowM: number;
  readonly returnM: number;
  readonly totalM: number;
  readonly runs: number;
}

/**
 * Pipe length by diameter and material, flow and return apart — the start of
 * a materials list. Plan length only: rises and drops between storeys are not
 * on a plan. A run with no material recorded is copper (`pipeMaterialOf`).
 */
export function pipeTotals(objects: readonly PlanObject[]): readonly PipeTotal[] {
  const by = new Map<string, { diameterMm: PipeDiameterMm; material: PlanPipeMaterial; flowM: number; returnM: number; runs: number }>();
  for (const object of objects) {
    if (object.kind !== "pipe") continue;
    const material = pipeMaterialOf(object.spec);
    const key = `${object.spec.diameterMm}:${material}`;
    const length = runLengthM(object.geometry.points);
    const entry = by.get(key) ?? { diameterMm: object.spec.diameterMm, material, flowM: 0, returnM: 0, runs: 0 };
    if (object.spec.circuit === "flow") entry.flowM += length;
    else entry.returnM += length;
    entry.runs += 1;
    by.set(key, entry);
  }
  const order = (m: PlanPipeMaterial) => PLAN_PIPE_MATERIALS.indexOf(m);
  return [...by.values()]
    .sort((a, b) => a.diameterMm - b.diameterMm || order(a.material) - order(b.material))
    .map((e) => ({ ...e, totalM: e.flowM + e.returnM }));
}

export interface PipeSnap {
  readonly point: Point;
  /** Along the pipe at that point, so a fitting sits in line with it. */
  readonly rotationDeg: number;
  readonly diameterMm: PipeDiameterMm;
  readonly material: PlanPipeMaterial;
  readonly pipeId: string;
  readonly at: "vertex" | "segment";
  /**
   * At a vertex or end where another run of a DIFFERENT size meets this one:
   * the sizes a reducer there joins, larger first. Undefined elsewhere.
   */
  readonly sizes?: { readonly largerMm: PipeDiameterMm; readonly smallerMm: PipeDiameterMm };
}

/**
 * The spec a fitting dropped on `snap` takes: the pipe's size and material,
 * and — for a reducer where two sizes meet — both sizes, larger first.
 */
export function snappedFittingSpec(spec: FittingSpec, snap: PipeSnap | undefined): FittingSpec {
  if (!snap) return spec;
  if (spec.fitting === "reducer") {
    const sizes = snap.sizes;
    const kept = spec.reducedDiameterMm != null && spec.reducedDiameterMm < snap.diameterMm ? spec.reducedDiameterMm : null;
    return {
      fitting: "reducer",
      diameterMm: sizes ? sizes.largerMm : snap.diameterMm,
      reducedDiameterMm: sizes ? sizes.smallerMm : kept,
      material: snap.material,
    };
  }
  return { fitting: spec.fitting, diameterMm: snap.diameterMm, material: snap.material };
}

/**
 * Where a fitting dropped at `point` goes: onto a pipe's corner or end within
 * `radiusM` (corners and ends win — that is where elbows and tees live), then
 * onto the nearest point of a pipe's run. Undefined with no pipe in reach.
 */
export function snapToPipe(
  point: Point,
  pipes: readonly Pick<Extract<PlanObject, { kind: "pipe" }>, "id" | "spec" | "geometry">[],
  radiusM: number,
): PipeSnap | undefined {
  const legAngle = (a: Point, b: Point): number =>
    normaliseDegrees(Math.atan2(b.y - a.y, b.x - a.x) / DEG);

  let vertex: PipeSnap | undefined;
  let vertexDistance = radiusM;
  for (const pipe of pipes) {
    const pts = pipe.geometry.points;
    pts.forEach((p, i) => {
      const d = Math.hypot(point.x - p.x, point.y - p.y);
      if (d > vertexDistance) return;
      const a = pts[i === 0 ? 0 : i - 1] as Point;
      const b = pts[i === 0 ? 1 : i] as Point;
      vertexDistance = d;
      vertex = {
        point: p,
        rotationDeg: legAngle(a, b),
        diameterMm: pipe.spec.diameterMm,
        material: pipeMaterialOf(pipe.spec),
        pipeId: pipe.id,
        at: "vertex",
      };
    });
  }
  if (vertex) {
    // Another run of a different size meeting here — where a reducer goes.
    const found: PipeSnap = vertex;
    const other = pipes.find(
      (p) =>
        p.id !== found.pipeId &&
        p.spec.diameterMm !== found.diameterMm &&
        p.geometry.points.some((q) => Math.hypot(q.x - found.point.x, q.y - found.point.y) <= radiusM),
    );
    if (!other) return found;
    const larger = Math.max(other.spec.diameterMm, found.diameterMm) as PipeDiameterMm;
    const smaller = Math.min(other.spec.diameterMm, found.diameterMm) as PipeDiameterMm;
    return { ...found, sizes: { largerMm: larger, smallerMm: smaller } };
  }

  let best: PipeSnap | undefined;
  let bestDistance = radiusM;
  for (const pipe of pipes) {
    const pts = pipe.geometry.points;
    for (let i = 1; i < pts.length; i += 1) {
      const a = pts[i - 1] as Point;
      const b = pts[i] as Point;
      const foot = closestPointOnSegmentM(point, a, b);
      const d = Math.hypot(point.x - foot.x, point.y - foot.y);
      if (d <= bestDistance) {
        bestDistance = d;
        best = {
          point: foot,
          rotationDeg: legAngle(a, b),
          diameterMm: pipe.spec.diameterMm,
          material: pipeMaterialOf(pipe.spec),
          pipeId: pipe.id,
          at: "segment",
        };
      }
    }
  }
  return best;
}

/** Every point a new pipe end may snap to: other runs' corners and ends, and fittings. */
export function pipeAnchors(objects: readonly PlanObject[]): readonly Point[] {
  const anchors: Point[] = [];
  for (const object of objects) {
    if (object.kind === "pipe") anchors.push(...object.geometry.points);
    else if (object.kind === "fitting") anchors.push({ x: object.geometry.x, y: object.geometry.y });
  }
  return anchors;
}

// ------------------------------------------------------------- captions ----

/** "Vaillant aroTHERM plus 7 kW · 1100 × 450 mm" — the name and the real footprint. */
export function heatPumpCaption(spec: HeatPumpSpec): string {
  return calcHeatPumpCaption(spec);
}

/** "K2 600×1200" */
export function radiatorCaption(spec: RadiatorSpec): string {
  return radiatorLabel(spec);
}

/** "22 mm flow", "22 mm flow · PEX barrier" — copper, the default, is not named. */
export function pipeName(spec: PipeSpec): string {
  const material = pipeMaterialOf(spec);
  return `${spec.diameterMm} mm ${spec.circuit}${material === "copper" ? "" : ` · ${PIPE_MATERIAL_NAMES[material]}`}`;
}

/** "Elbow · 15 mm", "Reducer · 22 × 15 mm" */
export function fittingCaption(spec: FittingSpec): string {
  const size = fittingSizeLabel(spec);
  return size === null ? FITTING_NAMES[spec.fitting] : `${FITTING_NAMES[spec.fitting]} · ${size}`;
}

/** One line naming an object, for the panel and for screen readers. */
export function objectCaption(object: PlanObject): string {
  switch (object.kind) {
    case "heat-pump":
      return heatPumpCaption(object.spec);
    case "radiator":
      return radiatorCaption(object.spec);
    case "pipe":
      return `${pipeName(object.spec)} · ${formatLength(runLengthM(object.geometry.points), "m")}`;
    case "fitting":
      return fittingCaption(object.spec);
    case "cylinder":
    case "buffer":
      return vesselLabel(object.spec);
  }
}

/** What the plan will put down on the next click: a kind and exactly what it is. */
export type ObjectTool =
  | { readonly kind: "heat-pump"; readonly spec: HeatPumpSpec }
  | { readonly kind: "radiator"; readonly spec: RadiatorSpec }
  | { readonly kind: "pipe"; readonly spec: PipeSpec }
  | { readonly kind: "fitting"; readonly spec: FittingSpec }
  | { readonly kind: "cylinder"; readonly spec: PlanCylinderSpec }
  | { readonly kind: "buffer"; readonly spec: PlanBufferSpec };

export const KIND_NAMES: Readonly<Record<PlanObject["kind"], string>> = PLAN_OBJECT_KIND_NAMES;
